import i18n from 'i18next'
import { all, takeEvery, put, call } from 'redux-saga/effects'
import store from 'store'
import qs from 'qs'
import { history, store as reduxStore } from 'index'
import { getSearchParams, setSearchParams } from 'utils'
import { getAvailableLocale } from 'localization'

import SettingsService from 'popups/Settings/api/Settings.ts'
import actions from './actions.ts'

let isPopupWin = false
const params = getSearchParams()

try {
  if (window.opener != null && window.opener.childWinTable != null && params.winId != null) {
    const dnObj = window.opener.childWinTable[params.winId]
    if (dnObj != null && dnObj.popup === true) {
      isPopupWin = true
    }
  }
} catch (e) {
  console.log('popupWin check failed.'.e)
}

let zeroDarkWebCheckTimer = null

const checkZeroDarkWebExpired = () => {
  const state = reduxStore.getState()
  const zdwSettings = state?.settings?.zeroDarkWebSettings

  if (
    !zdwSettings?.zdw_active ||
    zdwSettings?.zdw_active !== 'true' ||
    !zdwSettings?.zdw_expired_at
  ) {
    if (zeroDarkWebCheckTimer != null) {
      clearTimeout(zeroDarkWebCheckTimer)
      zeroDarkWebCheckTimer = null
    }
    return
  }

  const expiredTime = new Date(zdwSettings.zdw_expired_at).getTime()
  const curTime = Date.now()
  const remainingTime = expiredTime - curTime
  // console.log(' --- ZeroDarkWeb remain time : ', remainingTime / 1000)

  if (remainingTime <= 0) {
    console.log('🔄 ZeroDarkWeb expired, dispatching darkWebFetchData event...')
    window.dispatchEvent(
      new CustomEvent('darkWebFetchData', { detail: { timestamp: new Date().toISOString() } }),
    )

    if (zeroDarkWebCheckTimer != null) {
      clearTimeout(zeroDarkWebCheckTimer)
      zeroDarkWebCheckTimer = null
    }
  } else {
    const loopTime = 30 * 1000 // 30초
    const minInterval = 1000 // 1초
    const nextInterval = Math.min(Math.max(remainingTime, minInterval), loopTime)

    zeroDarkWebCheckTimer = setTimeout(() => {
      zeroDarkWebCheckTimer = null
      checkZeroDarkWebExpired()
    }, nextInterval)
  }
}

export function* CHANGE_SETTING({ payload: { setting, value, save } }) {
  const saveSetting = () => {
    if (isPopupWin === false) {
      store.set(`app.settings.${setting}`, value)
    }
  }

  if (save !== false) {
    yield saveSetting()
  }

  yield put({
    type: 'settings/SET_STATE',
    payload: {
      [setting]: value,
    },
  })

  if (setting === 'zeroDarkWebSettings') {
    if (zeroDarkWebCheckTimer != null) {
      clearTimeout(zeroDarkWebCheckTimer)
      zeroDarkWebCheckTimer = null
    }

    if (value?.zdw_active === 'true' && value?.zdw_expired_at) {
      setTimeout(() => {
        checkZeroDarkWebExpired()
      }, 0)
    }

    if (value?.zdw_active === 'false') {
      yield put({
        type: 'settings/CHANGE_SETTING',
        payload: {
          setting: 'showZeroDarkWeb',
          value: false,
        },
      })
    }
  }
}

export function* SET_IS_LEAKED({ payload: { isLeaked, save = true } }) {
  const saveSetting = () => {
    setSearchParams({ leaked: isLeaked ? 'true' : undefined })
  }

  if (save !== false) {
    yield saveSetting()
  }

  yield put({
    type: 'settings/SET_STATE',
    payload: {
      isLeaked,
    },
  })
}

export function* SET_IS_GRID({ payload: { isGrid, save } }) {
  const saveSetting = () => {
    if (isPopupWin === false) {
      store.set(`app.settings.isGrid`, isGrid)
    }
  }

  if (save !== false) {
    yield saveSetting()
  }

  yield put({
    type: 'settings/SET_STATE',
    payload: {
      isGrid,
    },
  })
}

export function* SET_THEME({ payload: { theme } }) {
  const nextTheme = theme === 'dark' ? 'dark' : 'default'
  yield document.querySelector('html').setAttribute('data-kit-theme', nextTheme)
  yield reduxStore.dispatch({
    type: 'settings/CHANGE_SETTING',
    payload: {
      setting: 'theme',
      value: nextTheme,
    },
  })
}

export function* SET_SETTINGS({
  payload: {
    locale,
    umkDuration,
    recoverKeyEnabled,
    isSendNotification,
    zeroDarkWebSettings,
    clipboardClearMs,
  },
}) {
  if (locale) {
    const availableLocale = getAvailableLocale(locale)
    const longLocale = {
      ko: 'ko-KR',
      ja: 'ja-JP',
      en: 'en-US',
    }

    store.set('app.settings.locale', longLocale[locale])
    const t = yield call(i18n.changeLanguage, availableLocale)
    if (t != null) {
      yield put({
        type: 'menu/RELOAD',
      })
    }

    yield put({
      type: 'settings/CHANGE_SETTING',
      payload: {
        setting: 'locale',
        value: availableLocale,
      },
    })
  }

  if (umkDuration != null) {
    yield put({
      type: 'settings/CHANGE_SETTING',
      payload: {
        setting: 'umkDuration',
        value: umkDuration,
      },
    })
  }

  if (clipboardClearMs != null) {
    yield put({
      type: 'settings/CHANGE_SETTING',
      payload: {
        setting: 'clipboardClearMs',
        value: clipboardClearMs,
      },
    })
  }

  if (recoverKeyEnabled != null) {
    yield put({
      type: 'settings/CHANGE_SETTING',
      payload: {
        setting: 'recoverKeyEnabled',
        value: recoverKeyEnabled,
      },
    })
  }

  if (isSendNotification != null) {
    yield put({
      type: 'settings/CHANGE_SETTING',
      payload: {
        setting: 'isSendNotification',
        value: isSendNotification,
      },
    })
  }

  if (zeroDarkWebSettings != null) {
    const zdwSettingStore = store.get('app.settings.zeroDarkWebSettings')

    const object = {
      ...zdwSettingStore,
      ...zeroDarkWebSettings,
    }
    // console.log(' --- object : ', object)

    yield call(SettingsService.put, {
      zeroDarkWebSettings: Object.entries(object).map(([key, value]) => ({
        label: key,
        content: String(value),
      })),
    })

    yield put({
      type: 'settings/CHANGE_SETTING',
      payload: {
        setting: 'zeroDarkWebSettings',
        value: object,
      },
    })

    if (zeroDarkWebSettings?.zdw_active === true) {
      yield put({
        type: 'settings/CHANGE_SETTING',
        payload: {
          setting: 'showZeroDarkWeb',
          value: true,
        },
      })
    }
  }
}

export function* SETUP() {
  const changeSettings = (search) => {
    const query = qs.parse(search, { ignoreQueryPrefix: true })
    Object.keys(query).forEach((key) => {
      let value
      switch (query[key]) {
        case 'false':
          value = false
          break
        case 'true':
          value = true
          break
        default:
          value = query[key]
          break
      }
      if (key === 'theme') {
        reduxStore.dispatch({
          type: 'settings/SET_THEME',
          payload: {
            theme: value,
          },
        })
      } else if (key === 'locale' || key === 'lang') {
        reduxStore.dispatch({
          type: 'settings/SET_SETTINGS',
          payload: {
            locale: value,
          },
        })
      }
    })
  }
  yield changeSettings(history.location.search)
  yield history.listen((sparams) => {
    const { search } = sparams
    changeSettings(search)
  })

  // init theme on app load
  const initTheme = () => {
    const { search } = history.location
    const query = qs.parse(search, { ignoreQueryPrefix: true })
    const theme = query.theme || store.get('app.settings.theme') || 'default'
    reduxStore.dispatch({
      type: 'settings/SET_THEME',
      payload: {
        theme,
      },
    })
  }
  yield initTheme()

  const initLocale = () => {
    const locale = store.get('app.settings.locale')
    if (locale) {
      reduxStore.dispatch({
        type: 'settings/CHANGE_SETTING',
        payload: {
          setting: 'locale',
          value: locale,
        },
      })
    }
  }
  yield initLocale()

  // detect isMobileView setting on app load and window resize
  const isMobileView = (load = false) => {
    const currentState = global.window.innerWidth < 768
    const prevState = store.get('app.settings.isMobileView')
    if (currentState !== prevState || load) {
      reduxStore.dispatch({
        type: 'settings/CHANGE_SETTING',
        payload: {
          setting: 'isMobileView',
          value: currentState,
        },
      })
    }
  }

  // detect viewport width on app load and window resize
  const isMenuToggled = () => {
    const shouldToggle = global.window.innerWidth < 1024
    const prevState = store.get('app.settings.isMenuCollapsed')
    if (shouldToggle || prevState) {
      reduxStore.dispatch({
        type: 'settings/CHANGE_SETTING',
        payload: {
          setting: 'isMenuCollapsed',
          value: true,
        },
      })
    }
  }

  yield isMobileView(false)
  yield isMenuToggled()
  yield window.addEventListener('resize', () => {
    isMobileView()
    isMenuToggled()
  })

  // ZeroDarkWeb 만료 시간 체크 시작 (앱 시작 시 기존 설정이 있으면)
  const initZeroDarkWebCheck = () => {
    const state = reduxStore.getState()
    const zdwSettings = state?.settings?.zeroDarkWebSettings
    if (zdwSettings?.zdw_active === 'true' && zdwSettings?.zdw_expired_at) {
      setTimeout(() => {
        checkZeroDarkWebExpired()
      }, 1000) // 앱 초기화 후 1초 뒤에 체크 시작
    }
  }
  yield initZeroDarkWebCheck()
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.CHANGE_SETTING, CHANGE_SETTING),
    takeEvery(actions.SET_IS_GRID, SET_IS_GRID),
    takeEvery(actions.SET_THEME, SET_THEME),
    takeEvery(actions.SET_SETTINGS, SET_SETTINGS),
    takeEvery(actions.SET_IS_LEAKED, SET_IS_LEAKED),
    SETUP(), // run once on app load to init listeners
  ])
}
